import { useCallback, useEffect, useRef, useState } from "react";
import { submitFeedback, withdrawFeedback, errorKeyFor, FEEDBACK_ERROR } from "../../services/feedbackApi";
import { RATE_LIMIT_COOLDOWN_MS } from "../../utils/feedback";

/**
 * The per-record state machine of the AI-reply feedback row (05a, FR-09 / FR-10 / FR-11).
 *
 *  - The thumb IS the signal: a click posts at once, and the thumb fills only after the 201 (no
 *    optimistic fill — a visitor who sees a filled thumb on a dropped request believes they were
 *    heard). The chip popover opens after that, and dismissing it never undoes the rating.
 *  - The other thumb flips (a new POST; the edge supersedes the prior rating); the same thumb
 *    clears (DELETE with the stored signalId; 404 NOT_FOUND / 409 NOT_OPEN = already cleared).
 *  - 403 / 401 / 404 CAPTURE_DISABLED hide the row for this record; 429 disables both thumbs for
 *    the edge's 60 s window; anything else is a retryable line. Nothing is queued or retried.
 *
 * State is per record and discarded on unmount: the widget has no read-back route (D8).
 */
export function useFeedbackRow({ recordId, conversationId }) {
	const [rating, setRating] = useState(null); // 'up' | 'down' | null — the ACTIVE signal
	const [signalId, setSignalId] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null); // chip popover anchor; null = closed
	const [thanks, setThanks] = useState(false);
	const [errorKey, setErrorKey] = useState(null);
	const [busy, setBusy] = useState(false);
	const [hidden, setHidden] = useState(false);
	const [limited, setLimited] = useState(false);

	const busyRef = useRef(false);
	const cooldownRef = useRef(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			clearTimeout(cooldownRef.current);
		};
	}, []);

	/** Run one write with the busy guard; a second click while one is in flight is ignored. */
	const run = useCallback(async (work) => {
		if (busyRef.current) return;
		busyRef.current = true;
		setBusy(true);
		setErrorKey(null);
		try {
			await work();
		} finally {
			busyRef.current = false;
			if (mountedRef.current) setBusy(false);
		}
	}, []);

	const startCooldown = useCallback(() => {
		setLimited(true);
		clearTimeout(cooldownRef.current);
		// Cleared on unmount, so it never fires into an unmounted row.
		cooldownRef.current = setTimeout(() => {
			setLimited(false);
			setErrorKey((prev) => (prev === FEEDBACK_ERROR.RATE_LIMITED ? null : prev));
		}, RATE_LIMIT_COOLDOWN_MS);
	}, []);

	/** Every failure lands here: it hides the row, starts the cooldown, or shows a line. */
	const fail = useCallback((err) => {
		if (!mountedRef.current) return;
		const key = errorKeyFor(err);
		if (key === FEEDBACK_ERROR.UNAVAILABLE) {
			setAnchorEl(null);
			setHidden(true);
			return;
		}
		if (key === FEEDBACK_ERROR.RATE_LIMITED) startCooldown();
		setErrorKey(key);
	}, [startCooldown]);

	const reset = () => {
		setRating(null);
		setSignalId(null);
		setThanks(false);
		setAnchorEl(null);
	};

	const clear = () => run(async () => {
		try {
			await withdrawFeedback({ signalId, conversationId });
			if (mountedRef.current) reset();
		} catch (err) {
			// Already withdrawn, or superseded by a flip from another tab: the rating is gone either way.
			if (errorKeyFor(err) === FEEDBACK_ERROR.GONE) {
				if (mountedRef.current) reset();
				return;
			}
			fail(err);
		}
	});

	/** A thumb click. `anchor` is the clicked button (captured before the await). */
	const onThumb = (next, anchor) => {
		if (limited || busyRef.current) return undefined;
		if (rating === next && signalId) return clear();
		return run(async () => {
			try {
				const { signalId: id } = await submitFeedback({ conversationId, recordId, rating: next, chips: [] });
				if (!mountedRef.current) return;
				setRating(next);
				setSignalId(id || null);
				setThanks(false);
				setAnchorEl(anchor || null);
			} catch (err) {
				fail(err);
			}
		});
	};

	/** Send from the popover: the SAME rating again, with chips (and the note when `other` is on). */
	const sendChips = ({ chips, freeText }) => run(async () => {
		try {
			const { signalId: id } = await submitFeedback({ conversationId, recordId, rating, chips, freeText });
			if (!mountedRef.current) return;
			if (id) setSignalId(id);
			setThanks(true);
			setAnchorEl(null);
		} catch (err) {
			fail(err);
		}
	});

	/** Skip, Escape or a backdrop click: close the popover and keep the rating. */
	const closeChips = useCallback(() => setAnchorEl(null), []);

	return { rating, signalId, anchorEl, thanks, errorKey, busy, hidden, limited, onThumb, sendChips, closeChips };
}
